import { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

const capModalTypes = ["sd-cap-1", "sd-cap-2", "sd-cap-3", "sd-cap-4", "sd-cap-5", "sd-cap-6"];

export default function FeaturesServiceDesk({ onOpenModal }: { onOpenModal?: (type: string) => void }) {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const features = useMemo(() => [
    {
      icon: "ri-customer-service-2-line",
      title: t("sd.features.f1.title"),
      description: t("sd.features.f1.desc"),
      stat: t("sd.features.f1.stat"),
      statLabel: t("sd.features.f1.statLabel"),
    },
    {
      icon: "ri-error-warning-line",
      title: t("sd.features.f2.title"),
      description: t("sd.features.f2.desc"),
      stat: t("sd.features.f2.stat"),
      statLabel: t("sd.features.f2.statLabel"),
    },
    {
      icon: "ri-git-branch-line",
      title: t("sd.features.f3.title"),
      description: t("sd.features.f3.desc"),
      stat: t("sd.features.f3.stat"),
      statLabel: t("sd.features.f3.statLabel"),
    },
    {
      icon: "ri-database-2-line",
      title: t("sd.features.f4.title"),
      description: t("sd.features.f4.desc"),
      stat: t("sd.features.f4.stat"),
      statLabel: t("sd.features.f4.statLabel"),
    },
    {
      icon: "ri-bar-chart-box-line",
      title: t("sd.features.f5.title"),
      description: t("sd.features.f5.desc"),
      stat: t("sd.features.f5.stat"),
      statLabel: t("sd.features.f5.statLabel"),
    },
    {
      icon: "ri-smartphone-line",
      title: t("sd.features.f6.title"),
      description: t("sd.features.f6.desc"),
      stat: t("sd.features.f6.stat"),
      statLabel: t("sd.features.f6.statLabel"),
    },
  ], [t]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="features" className="relative py-16 md:py-24 bg-background-950 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary-400/20 to-transparent" />

      <div className="w-full px-4 sm:px-6 lg:px-10 relative z-10">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent-400/20 bg-accent-500/8 mb-4">
            <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">
              {t("sd.features.tag")}
            </span>
          </div>
          <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-50 mb-3 tracking-tight">
            {t("sd.features.title")} <span className="text-accent-400">{t("sd.features.highlight")}</span>
          </h2>
          <p className="text-sm text-foreground-400/60 max-w-lg mx-auto leading-relaxed">
            {t("sd.features.subtitle")}
          </p>
        </div>

        <div
          className={`max-w-5xl mx-auto mb-10 md:mb-14 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="relative rounded-2xl overflow-hidden border border-foreground-100/8">
            <img
              src="https://readdy.ai/api/search-image?query=Friendly%20diverse%20IT%20support%20agents%20at%20a%20modern%20service%20desk%2C%20some%20wearing%20headsets%20and%20others%20typing%20on%20laptops%2C%20genuine%20warm%20expressions%2C%20bright%20collaborative%20workspace%20with%20multiple%20monitors%20showing%20dashboards%2C%20natural%20lighting%20mixed%20with%20blue%20screen%20glow%2C%20editorial%20photography%20style%2C%20candid%20human%20moment%2C%20soft%20warm%20tones%2C%20high%20detail%20professional%20environment&width=1200&height=400&seq=sd-human-banner&orientation=landscape"
              alt="Humanized Service Desk Team"
              className="w-full h-52 md:h-64 object-cover object-top"
              loading="lazy"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 via-background-950/30 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-6 md:px-10 max-w-lg">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500/15 border border-accent-400/20">
                    <i className="ri-heart-pulse-line text-lg text-accent-300" />
                  </div>
                  <span className="text-[10px] font-label font-semibold text-accent-300 uppercase tracking-[0.2em]">{t("sd.features.humanTag")}</span>
                </div>
                <h3 className="font-heading font-bold text-lg md:text-xl text-white mb-2">
                  {t("sd.features.humanTitle")}
                </h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  {t("sd.features.humanDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature, idx) => (
            <button
              type="button"
              key={feature.title}
              onClick={() => onOpenModal?.(capModalTypes[idx])}
              className={`group relative text-left w-full cursor-pointer rounded-xl border border-foreground-100/8 bg-background-950/40 backdrop-blur-sm p-5 hover:border-primary-400/20 transition-all duration-500 hover:-translate-y-1 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${200 + idx * 100}ms` }}
            >
              <div className="absolute top-0 left-3 right-3 h-[2px] bg-gradient-to-r from-transparent via-primary-500/0 to-transparent group-hover:via-primary-500/50 transition-all duration-400 rounded-full" />

              <div className="relative z-10">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 border border-primary-400/15 text-primary-400 mb-4 group-hover:bg-primary-500/15 group-hover:text-primary-300 transition-all duration-300">
                  <i className={`${feature.icon} text-lg`} />
                </div>

                <div className="flex items-baseline gap-2 mb-2">
                  <h3 className="font-heading font-semibold text-base text-foreground-100 group-hover:text-primary-300 transition-colors">
                    {feature.title}
                  </h3>
                  <span className="text-[10px] font-label text-primary-400/60 opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-arrow-right-up-line" />
                  </span>
                </div>

                <p className="text-sm text-foreground-400/60 leading-relaxed mb-4">
                  {feature.description}
                </p>

                <div className="flex items-center gap-2 pt-3 border-t border-foreground-100/5">
                  <span className="font-heading font-bold text-sm text-primary-400">{feature.stat}</span>
                  <span className="text-[10px] font-label text-foreground-500/40 uppercase tracking-wider">{feature.statLabel}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}